import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  Signal,
} from '@angular/core';
import { MatChip, MatChipRemove, MatChipSet } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { getMetadata, ref, Storage } from '@angular/fire/storage';
import { getDownloadURL$, NgCommonModule } from '@principle-theorem/ng-shared';
import { validateTotalAttachmentSize } from '@principle-theorem/ng-interactions';
import { ReferralMediaSelectionItemComponent } from './referral-media-selection-item/referral-media-selection-item.component';
import { IReferralMedia } from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  DocumentReference,
  isSameRef,
  shareReplayCold,
  WithRef,
} from '@principle-theorem/shared';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { validateReferralAttachment } from '../../services/referral-attachment-validation';

export interface IMediaWithThumbnail {
  media: WithRef<IReferralMedia>;
  thumbnailUrl$: Observable<string | undefined>;
  isImage: boolean;
  isSelected: Signal<boolean>;
  size: number;
  contentType: string;
  isValid: boolean;
  validationReason: string | undefined;
}

@Component({
  selector: 'pr-referral-media-selection',
  imports: [
    NgCommonModule,
    ReferralMediaSelectionItemComponent,
    MatChipSet,
    MatChip,
    MatChipRemove,
    MatButtonModule,
    MatIcon,
    MatProgressBarModule,
  ],
  templateUrl: './referral-media-selection.component.html',
  styleUrl: './referral-media-selection.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralMediaSelectionComponent {
  private _storage = inject(Storage);
  private _snackBar = inject(MatSnackBar);
  private _mediaMetadata = signal<
    Map<string, { size: number; contentType: string }>
  >(new Map());

  media = input<WithRef<IReferralMedia>[]>([]);
  selectedRefs = input<DocumentReference<IReferralMedia>[]>([]);
  selectionChange = output<DocumentReference<IReferralMedia>[]>();

  metadataLoaded = signal(false);

  hasMedia = computed(() => this.media().length > 0);

  allSelected = computed(() => {
    const validItems = this.mediaWithThumbnails().filter(
      (item) => item.isValid
    );
    return (
      validItems.length > 0 && validItems.every((item) => item.isSelected())
    );
  });

  selectedMediaWithThumbnails = computed(() =>
    this.mediaWithThumbnails().filter((item) => item.isSelected())
  );

  mediaWithThumbnails = computed<IMediaWithThumbnail[]>(() =>
    this._buildMediaWithThumbnails()
  );

  constructor() {
    effect(() => this._loadMediaMetadata());
  }

  onToggle(item: IMediaWithThumbnail): void {
    const currentRefs = this.selectedRefs();
    const isSelected = currentRefs.some((docRef) =>
      isSameRef(docRef, item.media.ref)
    );

    if (isSelected) {
      const newRefs = currentRefs.filter(
        (docRef) => !isSameRef(docRef, item.media.ref)
      );
      this.selectionChange.emit(newRefs);
      return;
    }

    if (!item.isValid) {
      if (item.validationReason) {
        this._snackBar.open(item.validationReason);
      }
      return;
    }

    const selectedItems = this.mediaWithThumbnails().filter((mediaItem) =>
      currentRefs.some((docRef) => isSameRef(docRef, mediaItem.media.ref))
    );
    const candidateAttachments = [
      ...selectedItems.map((mediaItem) => ({ size: mediaItem.size })),
      { size: item.size },
    ];
    const totalValidation = validateTotalAttachmentSize(candidateAttachments);
    if (!totalValidation.valid) {
      if (totalValidation.reason) {
        this._snackBar.open(totalValidation.reason);
      }
      return;
    }

    this.selectionChange.emit([...currentRefs, item.media.ref]);
  }

  toggleSelectAll(): void {
    if (this.allSelected()) {
      this.selectionChange.emit([]);
      return;
    }

    const validItems = this.mediaWithThumbnails().filter(
      (item) => item.isValid
    );
    const refsToSelect: DocumentReference<IReferralMedia>[] = [];
    let skippedCount = 0;

    for (const item of validItems) {
      const candidateAttachments = [
        ...refsToSelect.map((selectedRef) => {
          const mediaItem = this.mediaWithThumbnails().find((current) =>
            isSameRef(current.media.ref, selectedRef)
          );
          return { size: mediaItem?.size ?? 0 };
        }),
        { size: item.size },
      ];
      const totalValidation = validateTotalAttachmentSize(candidateAttachments);
      if (!totalValidation.valid) {
        skippedCount++;
        continue;
      }
      refsToSelect.push(item.media.ref);
    }

    const invalidCount = this.mediaWithThumbnails().length - validItems.length;
    const totalSkipped = skippedCount + invalidCount;
    if (totalSkipped > 0) {
      this._snackBar.open(
        `${totalSkipped} file(s) were not selected due to type or size restrictions`
      );
    }

    this.selectionChange.emit(refsToSelect);
  }

  removeFromSelection(item: WithRef<IReferralMedia>): void {
    const newRefs = this.selectedRefs().filter(
      (docRef) => !isSameRef(docRef, item.ref)
    );
    this.selectionChange.emit(newRefs);
  }

  getFileIcon(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return 'image';
      case 'doc':
      case 'docx':
        return 'description';
      default:
        return 'insert_drive_file';
    }
  }

  private _buildMediaWithThumbnails(): IMediaWithThumbnail[] {
    const metadataMap = this._mediaMetadata();
    return this.media().map((item) => {
      const isImage = this._isImageFile(item.name);
      const metadata = metadataMap.get(item.ref.path);
      const size = metadata?.size ?? 0;
      const contentType = metadata?.contentType ?? 'application/octet-stream';

      let isValid = !this.metadataLoaded() || !!metadata;
      let validationReason: string | undefined = isValid
        ? undefined
        : 'Unable to verify file — cannot attach to email';

      if (metadata) {
        const validation = validateReferralAttachment({
          name: item.name,
          size,
          contentType,
        });
        isValid = validation.valid;
        validationReason = validation.reason;
      }

      return {
        media: item,
        isImage,
        size,
        contentType,
        isValid,
        validationReason,
        isSelected: computed(() =>
          this.selectedRefs().some((docRef) => isSameRef(docRef, item.ref))
        ),
        thumbnailUrl$: isImage
          ? getDownloadURL$(this._storage, item.path).pipe(
              catchError(() => of(undefined)),
              shareReplayCold()
            )
          : of(undefined),
      };
    });
  }

  private _isImageFile(fileName: string): boolean {
    const ext = fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(
      ext ?? ''
    );
  }

  private _loadMediaMetadata(): void {
    const mediaItems = this.media();
    if (mediaItems.length > 0) {
      void this._fetchMetadata(mediaItems);
      return;
    }
    this.metadataLoaded.set(true);
  }

  private async _fetchMetadata(
    mediaItems: WithRef<IReferralMedia>[]
  ): Promise<void> {
    this.metadataLoaded.set(false);
    const metadataMap = new Map<
      string,
      { size: number; contentType: string }
    >();

    await asyncForEach(mediaItems, async (media) => {
      try {
        const storageRef = ref(this._storage, media.path);
        const metadata = await getMetadata(storageRef);
        metadataMap.set(media.ref.path, {
          size: metadata.size,
          contentType: metadata.contentType || 'application/octet-stream',
        });
      } catch {
        // Failed to fetch metadata — file will be marked invalid
      }
    });

    this._mediaMetadata.set(metadataMap);
    this.metadataLoaded.set(true);

    this._revalidateRestoredSelections();
  }

  private _revalidateRestoredSelections(): void {
    const currentRefs = this.selectedRefs();
    if (currentRefs.length === 0) {
      return;
    }

    const items = this.mediaWithThumbnails();
    const validRefs = currentRefs.filter((selectedRef) => {
      const item = items.find((current) =>
        isSameRef(current.media.ref, selectedRef)
      );
      if (!item || !item.isValid) {
        return false;
      }
      const otherSelectedItems = items.filter(
        (current) =>
          current.media.ref.path !== selectedRef.path &&
          currentRefs.some((docRef) => isSameRef(docRef, current.media.ref))
      );
      const candidateAttachments = [
        ...otherSelectedItems.map((media) => ({ size: media.size })),
        { size: item.size },
      ];
      const totalValidation = validateTotalAttachmentSize(candidateAttachments);
      return totalValidation.valid;
    });

    if (validRefs.length < currentRefs.length) {
      this._snackBar.open(
        'Some previously selected attachments are no longer valid and have been removed'
      );
      this.selectionChange.emit(validRefs);
    }
  }
}
